import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Checkbox,
  Divider,
  FormHelperText
} from '@material-ui/core';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import Autocomplete from '@material-ui/lab/Autocomplete';


const RequestPromotionDollarOff = ({
  values,
  setFieldValue,
  touched,
  errors
}) => {
  const { storeActiveCategoriesWithAllActiveParents, loadingCategories } = useSelector((state: IReduxState) => state.categories);
  const { store_products: { catalogActiveProducts }, productsLoading } = useSelector((state: IReduxState) => state.products);


  const renderOption = (option, { inputValue, type }) => {
    const optionText = type === 'categories' ? option?.nameWithPath : option.name;
    const matches = match(optionText, inputValue);
    const parts = parse(optionText, matches);

    return (
      <div>
        {parts.map((part, index) => (
          <span key={index} className={part.highlight ? 'text-primary font-bold' : ''}>
            {part.text}
          </span>
        ))}
        {type === 'listings' && Boolean(option.price) && (
          <span className="ml-2 text-green">${option.price}</span>
        )}
      </div>
    );
  };

  return (
    <Card className="p-5 mt-4" elevation={0}>
      <Typography variant="h5" className="font-bold mb-5">
        Applies To <span className="text-error">*</span>
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="applies to"
          name="dollarOff.appliesTo"
          value={values.dollarOff.appliesTo.name}
          onChange={(event) => {
            setFieldValue('dollarOff.appliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="Storewide"
            control={<Radio size="small" color="primary" />}
            label="Storewide"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.dollarOff.appliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.dollarOff.appliesTo.isCategories}
                  onChange={(e) => setFieldValue('dollarOff.appliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.dollarOff.appliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.dollarOff.appliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'dollarOff.appliesTo.value.categories',
                      value !== null ? value : values.dollarOff.appliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="dollarOff.appliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.dollarOff?.appliesTo?.value?.categories && Boolean(errors.dollarOff?.appliesTo?.value?.categories)}
                      helperText={touched.dollarOff?.appliesTo?.value?.categories && errors.dollarOff?.appliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.dollarOff.appliesTo.isListings}
                  onChange={(e) => setFieldValue('dollarOff.appliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.dollarOff.appliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.dollarOff.appliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'dollarOff.appliesTo.value.listings',
                      value !== null ? value : values.dollarOff.appliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="dollarOff.appliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.dollarOff?.appliesTo?.value?.listings && Boolean(errors.dollarOff?.appliesTo?.value?.listings)}
                      helperText={touched.dollarOff?.appliesTo?.value?.listings && errors.dollarOff?.appliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
          </div>
          }
          {(touched.dollarOff?.appliesTo?.isCategories ||
                        touched.dollarOff?.appliesTo?.isListings) &&
                        (Boolean(errors.dollarOff?.appliesTo?.isCategories) ||
                        Boolean(errors.dollarOff?.appliesTo?.isListings)) &&
                        <FormHelperText>
                          <Typography color="error" variant="caption">
                            At least one needs to be selected
                          </Typography>
                        </FormHelperText>
          }
          {touched.dollarOff?.appliesTo?.name && Boolean(errors.dollarOff?.appliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.dollarOff.appliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
      <Divider className="my-5" />
      <Typography variant="h5" className="font-bold mb-5">
        Does Not Apply To
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="not applies to"
          name="dollarOff.notAppliesTo"
          value={values.dollarOff.notAppliesTo.name}
          onChange={(event) => {
            setFieldValue('dollarOff.notAppliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="None"
            control={<Radio size="small" color="primary" />}
            label="None"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.dollarOff.notAppliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.dollarOff.notAppliesTo.isCategories}
                  onChange={(e) => setFieldValue('dollarOff.notAppliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.dollarOff.notAppliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.dollarOff.notAppliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'dollarOff.notAppliesTo.value.categories',
                      value !== null ? value : values.dollarOff.notAppliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="dollarOff.notAppliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.dollarOff?.notAppliesTo?.value?.categories && Boolean(errors.dollarOff?.notAppliesTo?.value?.categories)}
                      helperText={touched.dollarOff?.notAppliesTo?.value?.categories && errors.dollarOff?.notAppliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.dollarOff.notAppliesTo.isListings}
                  onChange={(e) => setFieldValue('dollarOff.notAppliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.dollarOff.notAppliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.dollarOff.notAppliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'dollarOff.notAppliesTo.value.listings',
                      value !== null ? value : values.dollarOff.notAppliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="dollarOff.notAppliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.dollarOff?.notAppliesTo?.value?.listings && Boolean(errors.dollarOff?.notAppliesTo?.value?.listings)}
                      helperText={touched.dollarOff?.notAppliesTo?.value?.listings && errors.dollarOff?.notAppliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
          </div>
          }
          {(touched.dollarOff?.notAppliesTo?.isCategories ||
            touched.dollarOff?.notAppliesTo?.isListings) &&
            (Boolean(errors.dollarOff?.notAppliesTo?.isCategories) ||
            Boolean(errors.dollarOff?.notAppliesTo?.isListings)) &&
            values.dollarOff.notAppliesTo.name === 'Specific Categories/Listings' &&
            <FormHelperText>
              <Typography color="error" variant="caption">
                At least one needs to be selected
              </Typography>
            </FormHelperText>
          }
          {touched.dollarOff?.notAppliesTo?.name && Boolean(errors.dollarOff?.notAppliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.dollarOff.notAppliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
    </Card>
  );
};

export default RequestPromotionDollarOff;
